package top.wcpe.allincore.dsl.ir.canvas

import top.wcpe.allincore.dsl.ir.codec.ByteArrayIrReader
import top.wcpe.allincore.dsl.ir.codec.IrByteReader
import kotlin.jvm.JvmStatic

/**
 * draw 字节数组 → 指令序列的解码器。
 *
 * 从原 `DrawBlob.decode` 拆出（M11.x 重构）；解析逻辑完全一致。
 * 底层由 [ByteArrayIrReader] 承载（大端、无符号读加宽），与旧 `ByteBuffer` 逐字节对齐。
 */
object DrawBlobDecoder {

    /** 从字节数组解码指令序列。 */
    @JvmStatic
    fun decode(bytes: ByteArray): List<Instruction> {
        if (bytes.isEmpty()) {
            return emptyList()
        }
        val r = ByteArrayIrReader(bytes)
        val count = r.readUnsignedShort()
        val result = ArrayList<Instruction>(count)
        for (i in 0 until count) {
            val op = r.readByte()
            when (op) {
                DrawOpcodes.OP_FOR_END -> result.add(InstructionFactory.forEnd())
                DrawOpcodes.OP_IF_END -> result.add(InstructionFactory.ifEnd())
                DrawOpcodes.OP_VAL_ASSIGN -> {
                    val slot = r.readUnsignedByte()
                    val value = decodeOperand(r)
                    result.add(InstructionFactory.valAssign(slot, value))
                }
                DrawOpcodes.OP_FOR_BEGIN -> {
                    val slot = r.readUnsignedByte()
                    val iter = r.readUnsignedShort()
                    val bodyLen = r.readUnsignedShort()
                    result.add(InstructionFactory.forBegin(slot, iter, bodyLen))
                }
                DrawOpcodes.OP_IF_BEGIN -> {
                    val cond = decodeOperand(r)
                    val thenLen = r.readUnsignedShort()
                    val elseLen = r.readUnsignedShort()
                    result.add(InstructionFactory.ifBegin(cond, thenLen, elseLen))
                }
                DrawOpcodes.OP_POLYGON -> {
                    val vertexCount = r.readUnsignedByte()
                    val operandCount = vertexCount * 2 + 1
                    // Array 的 init 按 0..n-1 顺序调用，与顺序读取语义一致
                    val operands = Array(operandCount) { decodeOperand(r) }
                    result.add(Instruction(DrawOpcodes.OP_POLYGON, operands))
                }
                DrawOpcodes.OP_PATH -> {
                    val vertexCount = r.readUnsignedByte()
                    val operandCount = vertexCount * 2 + 2
                    val operands = Array(operandCount) { decodeOperand(r) }
                    val closed = r.readUnsignedByte() != 0
                    result.add(Instruction(DrawOpcodes.OP_PATH, operands, closed))
                }
                DrawOpcodes.OP_PATH_BUILD -> {
                    // u8 cmdCount + cmdCount×(u8 cmdKind + N×Operand) + u8 modeByte
                    //   + widthOperand + colorOperand
                    val cmdCount = r.readUnsignedByte()
                    val kinds = ByteArray(cmdCount)
                    val coordOperands = ArrayList<Operand>()
                    for (j in 0 until cmdCount) {
                        val k = r.readByte()
                        kinds[j] = k
                        val n = DrawBlobSpec.pathNodeOperandCount(k)
                        for (x in 0 until n) {
                            coordOperands.add(decodeOperand(r))
                        }
                    }
                    val modeByte = r.readByte()
                    val width = decodeOperand(r)
                    val color = decodeOperand(r)
                    val operands = arrayOfNulls<Operand>(coordOperands.size + 2)
                    for (j in coordOperands.indices) {
                        operands[j] = coordOperands[j]
                    }
                    operands[operands.size - 2] = width
                    operands[operands.size - 1] = color
                    @Suppress("UNCHECKED_CAST")
                    result.add(
                        Instruction(
                            DrawOpcodes.OP_PATH_BUILD,
                            operands as Array<Operand>,
                            kinds,
                            modeByte,
                        ),
                    )
                }
                else -> {
                    val n = DrawBlobSpec.expectedOperandCount(op)
                    val operands = Array(n) { decodeOperand(r) }
                    result.add(Instruction(op, operands))
                }
            }
        }
        if (r.isReadable()) {
            throw IllegalStateException("DrawBlob 解码后剩余字节: ${r.readableBytes()}")
        }
        return result
    }

    private fun decodeOperand(r: IrByteReader): Operand {
        val kind = r.readUnsignedByte()
        return when (kind) {
            DrawOpcodes.OPERAND_LITERAL.toInt() -> Operand.literal(r.readDouble())
            DrawOpcodes.OPERAND_EXPR.toInt() -> {
                val len = r.readUnsignedShort()
                val exprBytes = ByteArray(len)
                r.readBytes(exprBytes)
                Operand.expr(exprBytes)
            }
            DrawOpcodes.OPERAND_STR.toInt() -> {
                val len = r.readUnsignedShort()
                val strBytes = ByteArray(len)
                r.readBytes(strBytes)
                Operand.str(strBytes.decodeToString())
            }
            DrawOpcodes.OPERAND_BYTES.toInt() -> {
                // v4 B.5：u16 byteLen + raw bytes（保留原 byte 不做 UTF-8 编解码）
                val len = r.readUnsignedShort()
                val raw = ByteArray(len)
                r.readBytes(raw)
                Operand.bytes(raw)
            }
            else -> throw IllegalStateException("未知操作数 kind: 0x${kind.toString(16)}")
        }
    }
}
